const API_BASE = import.meta.env.VITE_API_URL || 'https://humankind-bot.workers.dev/api';

interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
}

async function fetchApi<T>(
  endpoint: string,
  options?: RequestInit
): Promise<T> {
  const response = await fetch(`${API_BASE}${endpoint}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options?.headers,
    },
  });

  const result: ApiResponse<T> = await response.json();

  if (!result.success) {
    throw new Error(result.error || 'API request failed');
  }

  return result.data as T;
}

// User API
export const userApi = {
  auth: (data: {
    telegram_id: string;
    first_name: string;
    last_name?: string;
    username?: string;
  }) => fetchApi('/users/auth', { method: 'POST', body: JSON.stringify(data) }),

  getStats: (telegramId: string) =>
    fetchApi(`/users/${telegramId}/stats`),

  updateWallet: (telegramId: string, walletAddress: string) =>
    fetchApi(`/users/${telegramId}/wallet`, {
      method: 'PUT',
      body: JSON.stringify({ wallet_address: walletAddress }),
    }),

  getClaims: (telegramId: string) =>
    fetchApi(`/users/${telegramId}/claims`),

  getSubmissions: (telegramId: string) =>
    fetchApi(`/users/${telegramId}/submissions`),

  getTransactions: (telegramId: string) =>
    fetchApi(`/users/${telegramId}/transactions`),
};

// Tasks API
export const tasksApi = {
  list: (params?: { limit?: number; offset?: number; platform?: string }) => {
    const searchParams = new URLSearchParams();
    if (params?.limit) searchParams.set('limit', params.limit.toString());
    if (params?.offset) searchParams.set('offset', params.offset.toString());
    if (params?.platform) searchParams.set('platform', params.platform);
    return fetchApi(`/tasks?${searchParams}`);
  },

  get: (taskId: number, telegramId?: string) => {
    const params = telegramId ? `?telegram_id=${telegramId}` : '';
    return fetchApi(`/tasks/${taskId}${params}`);
  },

  claim: (taskId: number, telegramId: string) =>
    fetchApi(`/tasks/${taskId}/claim`, {
      method: 'POST',
      body: JSON.stringify({ telegram_id: telegramId }),
    }),

  submit: (
    taskId: number,
    telegramId: string,
    proof: { proof_url?: string; proof_text?: string; proof_screenshot?: string }
  ) =>
    fetchApi(`/tasks/${taskId}/submit`, {
      method: 'POST',
      body: JSON.stringify({ telegram_id: telegramId, ...proof }),
    }),

  generateReview: (taskId: number, telegramId: string) =>
    fetchApi<{ review: string }>(`/tasks/${taskId}/generate-review`, {
      method: 'POST',
      body: JSON.stringify({ telegram_id: telegramId }),
    }),
};

// Leaderboard API
export const leaderboardApi = {
  get: (limit = 10) => fetchApi(`/leaderboard?limit=${limit}`),
};

// Admin API
export const adminApi = {
  getPendingSubmissions: (telegramId: string) =>
    fetchApi(`/admin/submissions/pending?telegram_id=${telegramId}`),

  approveSubmission: (submissionId: number, telegramId: string, notes?: string) =>
    fetchApi(`/admin/submissions/${submissionId}/approve`, {
      method: 'POST',
      body: JSON.stringify({ telegram_id: telegramId, notes }),
    }),

  rejectSubmission: (submissionId: number, telegramId: string, notes?: string) =>
    fetchApi(`/admin/submissions/${submissionId}/reject`, {
      method: 'POST',
      body: JSON.stringify({ telegram_id: telegramId, notes }),
    }),

  createTask: (telegramId: string, task: any) =>
    fetchApi('/admin/tasks', {
      method: 'POST',
      body: JSON.stringify({ telegram_id: telegramId, ...task }),
    }),
};
